/**
 * 教学式提示引擎：基于玩家当前盘面，求"最简单的下一步"（docs/hint-feature-spec.md）。
 *
 * 技巧集是 puzzle-lab 推演引擎（difficulty/techniques/）的移植，语义以
 * difficulty/RULES.md 设计定稿为准，两侧不共享代码（同 SBN codec 的互认模式）。
 * 尝试顺序 = puzzle-lab 的 tier 顺序 = 玩家认知由易到难，命中即返回 ——
 * 给出的永远是当前盘面下最容易看出来的一步。
 *
 *   纠错  wrong_mark        （本站特有：白叉标在解星上）
 *   L1    adjacency         规则 2  邻接打叉
 *   L2    line_complete     规则 1  行/列已满
 *         region_complete   规则 3  区域已满
 *         last_cell         规则 4/5 行列/区域唯一空格
 *   L3    region_confined   规则 6  区域限行/列
 *         exclusion         规则 7  排除（一步假设-反驳）
 *         undercounting     规则 8  欠计数（k 区域占 k 线）
 *         overcounting      规则 9  过计数（k 线落 k 区域）
 *   L4    finned_counts     规则 11 带鳍计数（8/9 的差一格版）
 *         fish              规则 13 鱼（X-Wing / Swordfish）
 *         finned_fish       规则 14 带鳍鱼
 *         fish_sashimi      规则 14b 缺端点鱼
 *   兜底  reveal            规则 18 位（超纲：直接给一颗解星）
 *
 * 未移植：规则 10 受压排除（puzzle-lab 分析为冗余死规则）、规则 12 集合差分
 * （1 星语料增量为 0，与带鳍计数重叠）、规则 15-17 唯一性类（上游实现 unsound 已取消登记）。
 *
 * 健全性：wrong_mark 排在最前。本站双击对答案，星必然正确、红叉必在解外，
 * 唯一可能的玩家错误是白叉标在解星上 —— 只要先纠掉它，候选集必含全部剩余解星，
 * 于是"候选集上成立的排除"对真解同样成立（真解是候选约束的一个可行完成）。
 * 落星类结论额外用 isSolutionStar 复核，双保险。
 *
 * 返回结构：
 *   ruleId      技巧标识（对齐 puzzle-lab rule_id）
 *   highlight   本步推理涉及的格子（UI 描边用）
 *   fins        带鳍类技巧的"漏出格"（UI 用红圈区分，文案会点名它）
 *   actions     该步结论，[{r, c, to}]，to 为 cellState 值
 *   messageKey  i18n 文案 key（play.hint.<messageKey>）
 *   meta        结构信息，供文案填参与遥测归因。文案会直接引用这些字段，
 *               所以行/列号一律 1-based（row/col/line/lines/setLines/baseLines/
 *               confineLines），区域给 regionId（region/regions，UI 换算成色块）。
 */

import { EMPTY, STAR, WHITE_CROSS } from './cellState.js';

/** 鸽笼/鱼的规模上限，与 puzzle-lab MAX_PIGEONHOLE / MAX_FISH 一致。 */
const MAX_K = 7;

/** @typedef {{r: number, c: number, to: number}} HintAction */
/** @typedef {{ruleId: string, highlight: Array<{r: number, c: number}>, actions: HintAction[], messageKey: string, meta?: object}} Hint */

/**
 * 求下一步提示。对局已结束返回 null。
 * @param {import('./session.js').GameSession} session
 * @returns {Hint | null}
 */
export function nextHint(session) {
    if (session.isOver) return null;
    const g = new Grid(session);
    return wrongMark(g)
        ?? adjacency(g)
        ?? lineComplete(g)
        ?? regionComplete(g)
        ?? lastCell(g)
        ?? regionConfined(g)
        ?? exclusion(g)
        ?? undercounting(g)
        ?? overcounting(g)
        ?? finnedCounts(g)
        ?? fish(g)
        ?? finnedFish(g)
        ?? sashimiFish(g)
        ?? reveal(g);
}

// ── 盘面视图：把 session 折成技巧需要的候选集结构，每次提示只算一遍 ──

/** 单元格键，用于集合去重（dim ≤ 25，r*100+c 不会碰撞）。 */
const key = (r, c) => r * 100 + c;
const keyR = k => Math.floor(k / 100);
const keyC = k => k % 100;

class Grid {
    /** @param {import('./session.js').GameSession} session */
    constructor(session) {
        this.session = session;
        this.board = session.board;
        this.dim = session.dim;
        this.stars = session.board.stars;

        /** 候选格（空格）按行/列/区域索引；键为 key(r,c)。 */
        this.candByRow = Array.from({ length: this.dim }, () => []);
        this.candByCol = Array.from({ length: this.dim }, () => []);
        /** @type {Map<number, number[]>} regionId → 候选键 */
        this.candByRegion = new Map();
        /** @type {Map<number, number[]>} regionId → 全部格键 */
        this.cellsByRegion = new Map();
        /** @type {number[]} 全部候选键 */
        this.candidates = [];

        for (let r = 0; r < this.dim; r++) {
            for (let c = 0; c < this.dim; c++) {
                const g = this.board.regionAt(r, c);
                if (!this.cellsByRegion.has(g)) this.cellsByRegion.set(g, []);
                this.cellsByRegion.get(g).push(key(r, c));
                if (this.board.get(r, c) !== EMPTY) continue;
                const k = key(r, c);
                this.candidates.push(k);
                this.candByRow[r].push(k);
                this.candByCol[c].push(k);
                if (!this.candByRegion.has(g)) this.candByRegion.set(g, []);
                this.candByRegion.get(g).push(k);
            }
        }
    }

    regionAt(r, c) {
        return this.board.regionAt(r, c);
    }

    isCandidate(r, c) {
        return this.board.inBounds(r, c) && this.board.get(r, c) === EMPTY;
    }

    /** 该行/列/区域是否还缺星（已满的单元不参与推理）。 */
    rowNeedsStar(r) {
        return this.board.starsInRow[r] < this.stars;
    }

    colNeedsStar(c) {
        return this.board.starsInCol[c] < this.stars;
    }

    regionNeedsStar(g) {
        return this.board.starsInRegion[g] < this.stars;
    }

    /** 还缺星且尚有候选的区域：Map<regionId, 候选键[]>。 */
    openRegions() {
        const out = new Map();
        for (const [g, cand] of this.candByRegion) {
            if (this.regionNeedsStar(g) && cand.length) out.set(g, cand);
        }
        return out;
    }

    /** 按轴取某条线的候选键。axis: 'row' | 'col' */
    lineCand(axis, idx) {
        return axis === 'row' ? this.candByRow[idx] : this.candByCol[idx];
    }

    lineNeedsStar(axis, idx) {
        return axis === 'row' ? this.rowNeedsStar(idx) : this.colNeedsStar(idx);
    }

    /** 八邻格键集合（不含自身，越界剔除）。 */
    neighborKeys(r, c) {
        const out = new Set();
        for (let dr = -1; dr <= 1; dr++)
            for (let dc = -1; dc <= 1; dc++) {
                if (dr === 0 && dc === 0) continue;
                const nr = r + dr, nc = c + dc;
                if (nr >= 0 && nr < this.dim && nc >= 0 && nc < this.dim) out.add(key(nr, nc));
            }
        return out;
    }
}

// ── 工具 ──

/** 键集合 → {r,c} 数组（顺序稳定，便于测试与逐格动画）。 */
function toCells(keys) {
    return [...keys].sort((a, b) => a - b).map(k => ({ r: keyR(k), c: keyC(k) }));
}

/** 把键集合变成"打叉"动作。 */
function crossActions(keys) {
    return toCells(keys).map(p => ({ ...p, to: WHITE_CROSS }));
}

/** 组合枚举：从 arr 里取 k 个，回调返回 true 即提前终止。 */
function eachCombo(arr, k, fn) {
    const idx = [];
    const rec = start => {
        if (idx.length === k) return fn(idx.map(i => arr[i]));
        for (let i = start; i < arr.length; i++) {
            idx.push(i);
            if (rec(i + 1)) return true;
            idx.pop();
        }
        return false;
    };
    return rec(0);
}

/** 多个集合求交（传入 Set 数组），空数组返回空集。 */
function intersectAll(sets) {
    if (!sets.length) return new Set();
    let acc = sets[0];
    for (let i = 1; i < sets.length && acc.size; i++) {
        const next = new Set();
        for (const v of acc) if (sets[i].has(v)) next.add(v);
        acc = next;
    }
    return acc;
}

/** 若干条线上的候选键（去重）。 */
function candOfLines(g, axis, lines) {
    const out = new Set();
    for (const L of lines) for (const k of g.lineCand(axis, L)) out.add(k);
    return out;
}

const axisMsg = axis => (axis === 'row' ? 'row' : 'col');

// ── 纠错 ──

/** 白叉标在解星上 → 先纠错，否则后续推理会在错误候选集上进行。 */
function wrongMark(g) {
    const { session, board, dim } = g;
    for (let r = 0; r < dim; r++)
        for (let c = 0; c < dim; c++) {
            if (board.get(r, c) !== WHITE_CROSS) continue;
            if (!session.isSolutionStar(r, c)) continue;
            return {
                ruleId: 'wrong_mark',
                highlight: [{ r, c }],
                actions: [{ r, c, to: EMPTY }],
                messageKey: 'wrong_mark',
                meta: { row: r + 1, col: c + 1 },
            };
        }
    return null;
}

// ── L1 观察 ──

/**
 * 规则 2 邻接打叉：已放的星，八邻可叉。
 * 每行 1 星的关卡里顺带把同行同列一起清掉（等价于该星的 line_complete）——
 * 一句「同一行、同一列和周围一圈」比拆成三条提示好懂，也少点三次提示。
 */
function adjacency(g) {
    const { board, dim } = g;
    const full = g.stars === 1;
    for (let r = 0; r < dim; r++)
        for (let c = 0; c < dim; c++) {
            if (board.get(r, c) !== STAR) continue;
            const keys = new Set(g.neighborKeys(r, c));
            if (full) {
                for (const k of g.candByRow[r]) keys.add(k);
                for (const k of g.candByCol[c]) keys.add(k);
            }
            const empties = [...keys].filter(k => g.isCandidate(keyR(k), keyC(k)));
            if (!empties.length) continue;
            return {
                ruleId: 'adjacency',
                highlight: [{ r, c }, ...toCells(empties)],
                actions: crossActions(empties),
                messageKey: full ? 'adjacency_full' : 'adjacency',
                // 行列号只在「整行整列一起清」的版本里给（徽标据此点亮行列）
                meta: full ? { row: r + 1, col: c + 1 } : undefined,
            };
        }
    return null;
}

// ── L2 计数 ──

/** 规则 1 行/列已满：余格可叉。 */
function lineComplete(g) {
    for (const axis of ['row', 'col']) {
        for (let i = 0; i < g.dim; i++) {
            if (g.lineNeedsStar(axis, i)) continue;
            const cand = g.lineCand(axis, i);
            if (!cand.length) continue;
            return {
                ruleId: axis === 'row' ? 'row_complete' : 'col_complete',
                highlight: toCells(cand),
                actions: crossActions(cand),
                messageKey: axis === 'row' ? 'row_complete' : 'col_complete',
                meta: { axis, line: i + 1, stars: g.stars },
            };
        }
    }
    return null;
}

/** 规则 3 区域已满：余格可叉。 */
function regionComplete(g) {
    for (const [gid, cand] of g.candByRegion) {
        if (g.regionNeedsStar(gid)) continue;
        if (!cand.length) continue;
        return {
            ruleId: 'region_complete',
            highlight: toCells(g.cellsByRegion.get(gid)),
            actions: crossActions(cand),
            messageKey: 'region_complete',
            meta: { region: gid, stars: g.stars },
        };
    }
    return null;
}

/** 规则 4/5 唯一空格：行/列/区域只剩一个候选 → 必为星。 */
function lastCell(g) {
    const hit = (scopeKeys, cand, mk, meta) => {
        if (cand.length !== 1) return null;
        const r = keyR(cand[0]), c = keyC(cand[0]);
        // 健全性复核：wrong_mark 已排前，唯一候选必是解星
        if (!g.session.isSolutionStar(r, c)) return null;
        return {
            ruleId: 'last_cell',
            highlight: toCells(scopeKeys),
            actions: [{ r, c, to: STAR }],
            messageKey: mk,
            meta,
        };
    };

    for (let i = 0; i < g.dim; i++) {
        if (g.rowNeedsStar(i)) {
            const h = hit(g.candByRow[i], g.candByRow[i], 'last_cell_row', { axis: 'row', line: i + 1 });
            if (h) return h;
        }
        if (g.colNeedsStar(i)) {
            const h = hit(g.candByCol[i], g.candByCol[i], 'last_cell_col', { axis: 'col', line: i + 1 });
            if (h) return h;
        }
    }
    for (const [gid, cand] of g.candByRegion) {
        if (!g.regionNeedsStar(gid)) continue;
        const h = hit(g.cellsByRegion.get(gid), cand, 'last_cell_region', { region: gid });
        if (h) return h;
    }
    return null;
}

// ── L3 推演 ──

/** 规则 6 区域限行/列：区域候选全在单行/列 → 该线上区域外的格可叉。 */
function regionConfined(g) {
    for (const [gid, cand] of g.openRegions()) {
        if (cand.length < 2) continue; // 单候选由 last_cell 用更简单的话讲
        const rows = new Set(cand.map(keyR));
        const cols = new Set(cand.map(keyC));
        for (const [axis, lineset] of [['row', rows], ['col', cols]]) {
            if (lineset.size !== 1) continue;
            const line = [...lineset][0];
            const outside = g.lineCand(axis, line).filter(k => g.regionAt(keyR(k), keyC(k)) !== gid);
            if (!outside.length) continue;
            return {
                ruleId: 'region_confined',
                highlight: [...toCells(cand), ...toCells(outside)],
                actions: crossActions(outside),
                messageKey: axis === 'row' ? 'region_confined_row' : 'region_confined_col',
                meta: { axis, region: gid, line: line + 1 },
            };
        }
    }
    return null;
}

/**
 * 规则 7 排除：某待放星区域的全部候选都是格 X 的邻格 → X 放星会害死该区域 → X 可叉。
 * 一步"假设—反驳"。
 */
function exclusion(g) {
    for (const [gid, cand] of g.openRegions()) {
        const common = intersectAll(cand.map(k => g.neighborKeys(keyR(k), keyC(k))));
        if (!common.size) continue;
        const candSet = new Set(cand);
        const marks = [...common].filter(k => !candSet.has(k) && g.isCandidate(keyR(k), keyC(k)));
        if (!marks.length) continue;
        return {
            ruleId: 'exclusion',
            highlight: [...toCells(cand), ...toCells(marks)],
            actions: crossActions(marks),
            messageKey: 'exclusion',
            meta: { region: gid, candidateCount: cand.length },
        };
    }
    return null;
}

/**
 * 规则 8 欠计数：k 个区域的候选恰好占 k 条线 → 这些线上属于其它区域的格可叉。
 * （规则 6 是 k=1 的特例，故从 k=2 起。）
 */
function undercounting(g) {
    const regions = g.openRegions();
    const rids = [...regions.keys()];
    let found = null;
    const kMax = Math.min(MAX_K, rids.length - 1);

    for (let k = 2; k <= kMax && !found; k++) {
        for (const axis of ['row', 'col']) {
            if (found) break;
            eachCombo(rids, k, combo => {
                const lines = new Set();
                for (const rid of combo)
                    for (const ck of regions.get(rid)) lines.add(axis === 'row' ? keyR(ck) : keyC(ck));
                if (lines.size !== k) return false;

                const comboSet = new Set(combo);
                const marks = [];
                for (const L of lines)
                    for (const ck of g.lineCand(axis, L))
                        if (!comboSet.has(g.regionAt(keyR(ck), keyC(ck)))) marks.push(ck);
                if (!marks.length) return false;

                const structure = new Set();
                for (const rid of combo) for (const ck of regions.get(rid)) structure.add(ck);
                found = {
                    ruleId: 'undercounting',
                    highlight: [...toCells(structure), ...toCells(marks)],
                    actions: crossActions(new Set(marks)),
                    messageKey: `undercounting_${axisMsg(axis)}`,
                    meta: { axis, k, lines: [...lines].sort((a, b) => a - b).map(L => L + 1), regions: [...combo] },
                };
                return true;
            });
        }
    }
    return found;
}

/**
 * 规则 9 过计数（规则 8 的对偶）：k 条线的候选只落在 k 个区域 →
 * 这些区域里不在这 k 条线上的格可叉。
 */
function overcounting(g) {
    let found = null;

    for (const axis of ['row', 'col']) {
        /** 线号 → 该线候选覆盖的区域集合 */
        const lineRegions = new Map();
        for (let i = 0; i < g.dim; i++) {
            const cand = g.lineCand(axis, i);
            if (!cand.length) continue;
            lineRegions.set(i, new Set(cand.map(k => g.regionAt(keyR(k), keyC(k)))));
        }
        const lines = [...lineRegions.keys()];
        const kMax = Math.min(MAX_K, lines.length - 1);

        for (let k = 2; k <= kMax && !found; k++) {
            eachCombo(lines, k, combo => {
                const regions = new Set();
                for (const L of combo) for (const rid of lineRegions.get(L)) regions.add(rid);
                if (regions.size !== k) return false;

                const comboLines = new Set(combo);
                const onCombo = ck => comboLines.has(axis === 'row' ? keyR(ck) : keyC(ck));
                const marks = [];
                for (const rid of regions)
                    for (const ck of (g.candByRegion.get(rid) ?? []))
                        if (!onCombo(ck)) marks.push(ck);
                if (!marks.length) return false;

                found = {
                    ruleId: 'overcounting',
                    highlight: [...toCells(candOfLines(g, axis, combo)), ...toCells(marks)],
                    actions: crossActions(new Set(marks)),
                    messageKey: `overcounting_${axisMsg(axis)}`,
                    meta: { axis, k, lines: [...combo].sort((a, b) => a - b).map(L => L + 1), regions: [...regions] },
                };
                return true;
            });
        }
        if (found) break;
    }
    return found;
}

// ── L4 模式识别 ──

/**
 * 规则 11 带鳍计数：规则 8/9 的"差一格"版。
 * 候选几乎落在 k 条线/k 个区域内，只多出一条鳍线（或一个鳍区域）：
 *   鳍上无星 → 退化为干净欠/过计数，其排除成立；
 *   鳍上有星 → 鳍格的邻格可叉。
 * 两种情况都成立的格 = 干净排除集 ∩ 鳍格公共邻格 → 安全可叉。
 */
function finnedCounts(g) {
    const regions = g.openRegions();
    const rids = [...regions.keys()];

    for (let k = 2; k <= Math.min(MAX_K, Math.max(rids.length - 1, 1)); k++) {
        for (const axis of ['row', 'col']) {
            const hit = finnedUnder(g, regions, rids, k, axis);
            if (hit) return hit;
        }
        for (const axis of ['row', 'col']) {
            const hit = finnedOver(g, k, axis);
            if (hit) return hit;
        }
    }
    return null;
}

/** 带鳍欠计数：k 个区域的候选占 k+1 条线，去掉鳍线后仍每区域有候选。 */
function finnedUnder(g, regions, rids, k, axis) {
    let found = null;
    eachCombo(rids, k, combo => {
        /** 线号 → [{rid, key}] */
        const lines = new Map();
        for (const rid of combo)
            for (const ck of regions.get(rid)) {
                const L = axis === 'row' ? keyR(ck) : keyC(ck);
                if (!lines.has(L)) lines.set(L, []);
                lines.get(L).push({ rid, ck });
            }
        if (lines.size !== k + 1) return false; // 只处理恰好多出一条鳍线

        const comboSet = new Set(combo);
        for (const f of lines.keys()) {
            const base = [...lines.keys()].filter(L => L !== f);
            const finCells = lines.get(f).map(x => x.ck);

            // base k 条线必须仍覆盖全部 k 个区域，否则干净欠计数不成立
            const covered = new Set();
            for (const L of base) for (const x of lines.get(L)) covered.add(x.rid);
            if (covered.size !== k) continue;

            const clean = new Set();
            for (const L of base)
                for (const ck of g.lineCand(axis, L))
                    if (!comboSet.has(g.regionAt(keyR(ck), keyC(ck)))) clean.add(ck);
            if (!clean.size) continue;

            const commonNb = intersectAll(finCells.map(ck => g.neighborKeys(keyR(ck), keyC(ck))));
            if (!commonNb.size) continue;

            const marks = [...clean].filter(ck => commonNb.has(ck) && g.isCandidate(keyR(ck), keyC(ck)));
            if (!marks.length) continue;

            const structure = new Set(finCells);
            for (const rid of combo) for (const ck of regions.get(rid)) structure.add(ck);
            found = {
                ruleId: 'finned_counts',
                highlight: [...toCells(structure), ...toCells(marks)],
                fins: toCells(finCells),
                actions: crossActions(new Set(marks)),
                messageKey: `finned_counts_${axisMsg(axis)}`,
                meta: {
                    axis, k, fin: finCells.length, dir: 'under',
                    lines: base.sort((a, b) => a - b).map(L => L + 1),
                    regions: [...combo],
                },
            };
            return true;
        }
        return false;
    });
    return found;
}

/** 带鳍过计数（对偶）：k 条线的候选落在 k+1 个区域，去掉鳍区域后仍每线有候选。 */
function finnedOver(g, k, axis) {
    const lineCells = new Map();
    for (let i = 0; i < g.dim; i++) {
        const cand = g.lineCand(axis, i);
        if (cand.length) lineCells.set(i, cand);
    }
    const lines = [...lineCells.keys()];
    let found = null;

    eachCombo(lines, k, combo => {
        /** rid → 该区域落在这 k 条线上的候选 */
        const inRegions = new Map();
        for (const L of combo)
            for (const ck of lineCells.get(L)) {
                const rid = g.regionAt(keyR(ck), keyC(ck));
                if (!inRegions.has(rid)) inRegions.set(rid, []);
                inRegions.get(rid).push(ck);
            }
        if (inRegions.size !== k + 1) return false; // 恰好多出一个鳍区域

        const comboLines = new Set(combo);
        const lineOf = ck => (axis === 'row' ? keyR(ck) : keyC(ck));

        for (const fin of inRegions.keys()) {
            const baseRegions = [...inRegions.keys()].filter(rid => rid !== fin);
            const finCells = inRegions.get(fin);

            const coveredLines = new Set();
            for (const rid of baseRegions) for (const ck of inRegions.get(rid)) coveredLines.add(lineOf(ck));
            if (coveredLines.size !== k) continue;

            const clean = new Set();
            for (const rid of baseRegions)
                for (const ck of (g.candByRegion.get(rid) ?? []))
                    if (!comboLines.has(lineOf(ck))) clean.add(ck);
            if (!clean.size) continue;

            const commonNb = intersectAll(finCells.map(ck => g.neighborKeys(keyR(ck), keyC(ck))));
            if (!commonNb.size) continue;

            const marks = [...clean].filter(ck => commonNb.has(ck) && g.isCandidate(keyR(ck), keyC(ck)));
            if (!marks.length) continue;

            const structure = new Set(finCells);
            for (const L of combo) for (const ck of lineCells.get(L)) structure.add(ck);
            found = {
                ruleId: 'finned_counts',
                highlight: [...toCells(structure), ...toCells(marks)],
                fins: toCells(finCells),
                actions: crossActions(new Set(marks)),
                messageKey: `finned_counts_${axisMsg(axis)}`,
                meta: {
                    axis, k, fin: finCells.length, dir: 'over',
                    lines: [...combo].sort((a, b) => a - b).map(L => L + 1),
                    regions: baseRegions,
                },
            };
            return true;
        }
        return false;
    });
    return found;
}

/**
 * 鱼类共用的枚举底座：把 setAxis 的每条线折成"候选落在另一轴的哪些线"。
 * setAxis='col' → 列 i 的候选行集合；setAxis='row' → 行 i 的候选列集合。
 */
function fishCandidates(g, setAxis) {
    const cand = new Map();
    for (let i = 0; i < g.dim; i++) {
        if (!g.lineNeedsStar(setAxis, i)) continue;
        const cells = g.lineCand(setAxis, i);
        if (!cells.length) continue;
        cand.set(i, new Set(cells.map(ck => (setAxis === 'col' ? keyR(ck) : keyC(ck)))));
    }
    return cand;
}

const otherAxis = a => (a === 'col' ? 'row' : 'col');

/**
 * 规则 13 鱼：n 条线（列/行）的星都只能落在同样的 n 条另轴线里 →
 * 那 n 条另轴线上、不属于这 n 条线的格可叉。n=2 X-Wing，n=3 Swordfish。
 */
function fish(g) {
    for (let n = 2; n <= Math.min(MAX_K, g.dim - 1); n++) {
        for (const setAxis of ['col', 'row']) {
            const cand = fishCandidates(g, setAxis);
            const lines = [...cand.keys()];
            if (lines.length < n) continue;
            let found = null;

            eachCombo(lines, n, combo => {
                const confine = new Set();
                for (const i of combo) for (const j of cand.get(i)) confine.add(j);
                if (confine.size !== n) return false;

                const comboSet = new Set(combo);
                const other = otherAxis(setAxis);
                const marks = [];
                for (const j of confine)
                    for (const ck of g.lineCand(other, j)) {
                        const setIdx = setAxis === 'col' ? keyC(ck) : keyR(ck);
                        if (!comboSet.has(setIdx)) marks.push(ck);
                    }
                if (!marks.length) return false;

                const structure = new Set();
                for (const i of combo) for (const ck of g.lineCand(setAxis, i)) structure.add(ck);
                found = {
                    ruleId: 'fish',
                    highlight: [...toCells(structure), ...toCells(marks)],
                    actions: crossActions(new Set(marks)),
                    messageKey: setAxis === 'col' ? 'fish_col' : 'fish_row',
                    meta: {
                        axis: setAxis, n,
                        setLines: [...combo].sort((a, b) => a - b).map(L => L + 1),
                        confineLines: [...confine].sort((a, b) => a - b).map(L => L + 1),
                    },
                };
                return true;
            });
            if (found) return found;
        }
    }
    return null;
}

/**
 * 规则 14 带鳍鱼 / 14b 缺端点鱼：鱼的"差一格"版。
 * n 条线的候选占 n+1 条另轴线，去掉鳍线后仍每条线有候选：
 * 安全排除 = 干净鱼排除集 ∩ 鳍格公共邻格。
 * sashimi=true 时额外要求"至少一条线不含鳍" —— 即 X-Wing 端点缺失的变体。
 */
function finnedFishImpl(g, sashimi) {
    for (let n = 2; n <= Math.min(MAX_K, g.dim - 1); n++) {
        for (const setAxis of ['col', 'row']) {
            const cand = fishCandidates(g, setAxis);
            const lines = [...cand.keys()];
            if (lines.length < n) continue;
            const other = otherAxis(setAxis);
            let found = null;

            eachCombo(lines, n, combo => {
                const confine = new Set();
                for (const i of combo) for (const j of cand.get(i)) confine.add(j);
                if (confine.size !== n + 1) return false; // 恰好多出一条鳍线

                const comboSet = new Set(combo);
                for (const f of confine) {
                    const base = new Set([...confine].filter(j => j !== f));
                    // 每条集合线在 base 上仍有候选，否则干净鱼不成立
                    if (combo.some(i => ![...cand.get(i)].some(j => base.has(j)))) continue;

                    const hasMissing = combo.some(i => !cand.get(i).has(f));
                    if (sashimi ? !hasMissing : hasMissing) continue;

                    const finCells = combo
                        .filter(i => cand.get(i).has(f))
                        .map(i => (setAxis === 'col' ? key(f, i) : key(i, f)));

                    const clean = new Set();
                    for (const j of base)
                        for (const ck of g.lineCand(other, j)) {
                            const setIdx = setAxis === 'col' ? keyC(ck) : keyR(ck);
                            if (!comboSet.has(setIdx)) clean.add(ck);
                        }
                    if (!clean.size) continue;

                    const commonNb = intersectAll(finCells.map(ck => g.neighborKeys(keyR(ck), keyC(ck))));
                    if (!commonNb.size) continue;

                    const marks = [...clean].filter(ck => commonNb.has(ck) && g.isCandidate(keyR(ck), keyC(ck)));
                    if (!marks.length) continue;

                    const structure = new Set(finCells);
                    for (const i of combo) for (const ck of g.lineCand(setAxis, i)) structure.add(ck);
                    found = {
                        ruleId: sashimi ? 'fish_sashimi' : 'finned_fish',
                        highlight: [...toCells(structure), ...toCells(marks)],
                        fins: toCells(finCells),
                        actions: crossActions(new Set(marks)),
                        messageKey: `${sashimi ? 'fish_sashimi' : 'finned_fish'}_${setAxis === 'col' ? 'col' : 'row'}`,
                        meta: {
                            axis: setAxis, n, fin: finCells.length,
                            setLines: [...combo].sort((a, b) => a - b).map(L => L + 1),
                            baseLines: [...base].sort((a, b) => a - b).map(L => L + 1),
                        },
                    };
                    return true;
                }
                return false;
            });
            if (found) return found;
        }
    }
    return null;
}

/** 规则 14 带鳍鱼。 */
function finnedFish(g) {
    return finnedFishImpl(g, false);
}

/** 规则 14b 缺端点鱼（sashimi）。 */
function sashimiFish(g) {
    return finnedFishImpl(g, true);
}

// ── 兜底 ──

/** 规则 18 位：命名技巧全推不动（真正超纲），直接给一颗解星。 */
function reveal(g) {
    for (let r = 0; r < g.dim; r++) {
        if (!g.rowNeedsStar(r)) continue;
        const c = g.session.solution[r];
        if (!g.isCandidate(r, c)) continue;
        return {
            ruleId: 'reveal',
            highlight: [{ r, c }],
            actions: [{ r, c, to: STAR }],
            messageKey: 'reveal',
        };
    }
    return null;
}
